interface ProcessStep {
  number: string;
  title: string;
  desc: string;
  icon: string;
}

interface SharedProcessProps {
  title?: string;
  subtitle?: string;
  steps?: ProcessStep[];
}

const DEFAULT_STEPS: ProcessStep[] = [
  { number: '01', icon: 'ri-file-text-line', title: 'Request a Quote', desc: 'Fill out our simple form or call us. We respond within 24 hours with next steps and scheduling options.' },
  { number: '02', icon: 'ri-walk-line', title: 'On-Site Walkthrough', desc: 'Our team visits your facility to assess size, layout, and specific cleaning requirements at no charge.' },
  { number: '03', icon: 'ri-clipboard-line', title: 'Custom Cleaning Plan', desc: 'We build a detailed, customized cleaning plan around your schedule, standards, and budget needs.' },
  { number: '04', icon: 'ri-calendar-check-line', title: 'Scheduled Service Begins', desc: 'Trained, uniformed crew arrives on time, every visit — consistent staff who know your facility.' },
  { number: '05', icon: 'ri-checkbox-circle-line', title: 'Ongoing Quality Checks', desc: 'Regular inspections and open communication ensure consistent quality and satisfaction on every visit.' },
];

export default function SharedProcess({ title = 'Our Proven Cleaning Process', subtitle = 'From first contact to ongoing service — a transparent, professional process every step of the way.', steps = DEFAULT_STEPS }: SharedProcessProps) {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-full px-4 py-1.5 mb-5">
            <span className="text-accent text-xs font-body font-semibold uppercase tracking-wider">How It Works</span>
          </div>
          <h2 className="font-heading font-extrabold text-navy text-4xl xl:text-5xl mb-4">{title}</h2>
          <p className="text-gray-500 font-body text-lg max-w-2xl mx-auto">{subtitle}</p>
        </div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative flex flex-col items-center text-center group">
                <div className="relative z-10 w-20 h-20 flex items-center justify-center bg-navy rounded-2xl mb-5 group-hover:bg-accent transition-all duration-300">
                  <i className={`${step.icon} text-accent group-hover:text-navy text-2xl transition-colors duration-300`}></i>
                  <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-accent group-hover:bg-navy text-navy group-hover:text-accent text-xs font-heading font-extrabold rounded-full transition-all duration-300">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-navy text-base mb-2">{step.title}</h3>
                <p className="text-gray-500 font-body text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
